import { UnitModel, DeleteUnitModel } from "../UnitModels";
import { Reducer } from "redux";
import { ResourceLoadingState, DeleteResourceState, AddEditResourceState, SelectResourceState, CachedFiltersState, SendEmailState } from "../../common/store/stateHelpers";
import { FilterInfo, EditMode, RouteModel } from "../../common/models/CommonModels";

// Actions
export enum ActionKeys {
    RequestFreeUnits = "UNITS/REQUEST_FREE_UNITS",
    ReceivedFreeUnits = "UNITS/RECEIVED_FREE_UNITS",
    AddNewFreeUnit = "UNITS/ADD_NEW",
    AddNewFreeUnitStarted = "UNITS/ADD_NEW_STARTED",
    AddNewFreeUnitSuccess = "UNITS/ADD_NEW_SUCCESS",
    AddNewFreeUnitCancelled = "UNITS/ADD_NEW_CANCELLED",
    AddNewFormSubmitted = "UNITS/FORMS/ADD_NEW_SUBMITTED",
    AddNewFormCancelled = "UNITS/FORMS/ADD_NEW_CANCELLED",
    DeleteUnit = "UNITS/DELETE",
    DeleteStarted = "UNITS/DELETE_STARTED",
    DeleteSubmitted = "UNITS/DELETE_SUBMITTED",
    DeleteCancelled = "UNITS/DELETE_CANCELLED",
    DeleteSuccess = "UNITS/DELETE_SUCCESS",
    DeleteError = "UNITS/DELETE_ERROR",
    CopyUnit = "UNITS/COPY",
    CopyUnitStarted = "UNITS/COPY_STARTED",
    CopyUnitSuccess = "UNITS/COPY_SUCCESS",
    CacheFilters = "UNITS/CACHE_FILTERS",
    UnitSelected = "UNITS/SELECT_UNIT",
    ReceivedUnitRoutes = "UNITS/RECEIVED_ROUTES",
    SendEmail = "UNITS/SEND_EMAIL",
    SendEmailStarted = "UNITS/SEND_EMAIL_STARTED",
    SendEmailSubmitted = "UNITS/SEND_EMAIL_SUBMITTED",
    SendEmailCancelled = "UNITS/SEND_EMAIL_CANCELLED",
    SendEmailSuccess = "UNITS/SEND_EMAIL_SUCCESS",
    SendEmailError = "UNITS/SEND_EMAIL_ERROR",
    SendEmailToReporter = "UNITS/SEND_EMAIL_TO_REPORTER",
    SendEmailToReporterStarted = "UNITS/SEND_EMAIL_TO_REPORTER_STARTED",
    SendEmailToReporterSubmitted = "UNITS/SEND_EMAIL_TO_REPORTER_SUBMITTED",
    SendEmailToReporterSuccess = "UNITS/SEND_EMAIL_TO_REPORTER_SUCCESS",
    SendEmailToReporterCancelled = "UNITS/SEND_EMAIL_TO_REPORTER_CANCELLED",
    SendEmailToReporterError = "UNITS/SEND_EMAIL_TO_REPORTER_ERROR",
    ResetAddNewUnitFormTrailerIdentifier = "UNITS/RESET_ADD_NEW_UNIT_FORM_TRAILERIDENTIFIER"
};

export type UnitAction = AddEditUnitStartedAction
    | AddNewFreeUnitSuccessAction
    | AddNewFormCancelledAction
    | FreeUnitsRequestAction
    | FreeUnitsReceivedAction
    | DeleteUnitStartedAction
    | DeleteUnitSuccessAction
    | SelectUnitAction
    | ReceivedUnitRoutesAction
    | CacheFiltersAction
    | ResetAddNewUnitFormTrailerIdentifierAction;

export interface FreeUnitsRequestAction {
    type: ActionKeys.RequestFreeUnits;
    filters: FilterInfo
    includeRoutes?: boolean;
}

export interface ResetAddNewUnitFormTrailerIdentifierAction {
    type: ActionKeys.ResetAddNewUnitFormTrailerIdentifier
}

interface FreeUnitsReceivedAction {
    type: ActionKeys.ReceivedFreeUnits,
    data: UnitModel[]
}

/**
 * Add or edit unit.
 */
export interface AddEditUnitAction {
    type: ActionKeys.AddNewFreeUnit;
    unit?: UnitModel;
    afterAddOrEdit?: () => void;
    reloadUnits?: boolean;
}

interface AddEditUnitStartedAction {
    type: ActionKeys.AddNewFreeUnitStarted,
    unit?: UnitModel
}

interface AddNewFreeUnitSuccessAction {
    type: ActionKeys.AddNewFreeUnitSuccess
}

interface AddNewFormSubmittedAction {
    type: ActionKeys.AddNewFormSubmitted,
    data: UnitModel
}

interface AddNewFormCancelledAction {
    type: ActionKeys.AddNewFormCancelled
}

interface CacheFiltersAction {
    type: ActionKeys.CacheFilters;
    filters: FilterInfo
}

export interface DeleteUnitAction {
    type: ActionKeys.DeleteUnit;
    unit: UnitModel;
    afterDelete?: () => void;
}

interface DeleteUnitStartedAction {
    type: ActionKeys.DeleteStarted;
    unit: UnitModel
}

interface DeleteUnitSuccessAction {
    type: ActionKeys.DeleteSuccess;
}

interface DeleteUnitSubmittedAction {
    type: ActionKeys.DeleteSubmitted;
    data: DeleteUnitModel
}

interface DeleteUnitCancelledAction {
    type: ActionKeys.DeleteCancelled
}
interface SelectUnitAction {
    type: ActionKeys.UnitSelected;
    selected: UnitModel | null;
}

export interface CopyAction {
    type: ActionKeys.CopyUnit;
    unit: UnitModel;
}

interface CopyStartedAction {
    type: ActionKeys.CopyUnitStarted;
    unit: UnitModel;
}

interface CopySuccessAction {
    type: ActionKeys.CopyUnitSuccess;
}

interface ReceivedUnitRoutesAction {
    type: ActionKeys.ReceivedUnitRoutes;
    routes: RouteModel[]
}

export interface SendEmailAction {
    type: ActionKeys.SendEmail;
    unit: UnitModel;
}

interface SendEmailStartedAction {
    type: ActionKeys.SendEmailStarted;
    unit: UnitModel;
}

interface SendEmailSubmittedAction {
    type: ActionKeys.SendEmailSubmitted;
    data: any;
}

interface SendEmailSuccessAction {
    type: ActionKeys.SendEmailSuccess;
}

interface SendEmailErrorAction {
    type: ActionKeys.SendEmailError;
}

interface SendEmailCancelledAction {
    type: ActionKeys.SendEmailCancelled;
}

export interface SendEmailToReporterAction {
    type: ActionKeys.SendEmailToReporter;
    unit: UnitModel;
}

interface SendEmailToReporterStartedAction {
    type: ActionKeys.SendEmailToReporterStarted;
    unit: UnitModel;
}

interface SendEmailToReporterSuccessAction {
    type: ActionKeys.SendEmailToReporterSuccess;
}

interface SendEmailToReporterCancelledAction {
    type: ActionKeys.SendEmailToReporterCancelled;
}

interface SendEmailToReporterSubmittedAction {
    type: ActionKeys.SendEmailToReporterSubmitted;
    data: any;
}

interface SendEmailToReporterErrorAction {
    type: ActionKeys.SendEmailToReporterError;
}



export const actionCreators = {
    requestFreeUnits: (filters: FilterInfo, includeRoutes?: boolean) => <FreeUnitsRequestAction>{ type: ActionKeys.RequestFreeUnits, filters, includeRoutes },
    receivedFreeUnits: (payload: UnitModel[]) => <FreeUnitsReceivedAction>{ type: ActionKeys.ReceivedFreeUnits, data: payload },
    addOrEditUnit: (unit?: UnitModel, afterAddOrEdit?: () => void, reloadUnits?: boolean) => <AddEditUnitAction>{ type: ActionKeys.AddNewFreeUnit, unit, afterAddOrEdit, reloadUnits },
    addOrEditUnitStarted: (unit?: UnitModel) => <AddEditUnitStartedAction>{ type: ActionKeys.AddNewFreeUnitStarted, unit },
    addOrEditUnitSuccess: () => <AddNewFreeUnitSuccessAction>{ type: ActionKeys.AddNewFreeUnitSuccess },
    addOrEditFormSubmitted: (values: UnitModel) => <AddNewFormSubmittedAction>{ type: ActionKeys.AddNewFormSubmitted, data: values },
    addOrEditFormCancelled: () => <AddNewFormCancelledAction>{ type: ActionKeys.AddNewFormCancelled },
    cacheFilters: (filters: FilterInfo) => <CacheFiltersAction>{ type: ActionKeys.CacheFilters, filters },
    deleteUnit: (unit: UnitModel, afterDelete?: () => void) => <DeleteUnitAction>{ type: ActionKeys.DeleteUnit, unit, afterDelete },
    deleteStarted: (unit: UnitModel) => <DeleteUnitStartedAction>{ type: ActionKeys.DeleteStarted, unit },
    deleteSuccess: () => <DeleteUnitSuccessAction>{ type: ActionKeys.DeleteSuccess },
    deleteSubmitted: (data: DeleteUnitModel) => <DeleteUnitSubmittedAction>{ type: ActionKeys.DeleteSubmitted, data },
    deleteCancelled: () => <DeleteUnitCancelledAction>{ type: ActionKeys.DeleteCancelled },
    copyUnit: (unit: UnitModel) => <CopyAction>{ type: ActionKeys.CopyUnit, unit },
    copyUnitStarted: (unit: UnitModel) => <CopyStartedAction>{ type: ActionKeys.CopyUnitStarted, unit },
    copyUnitSuccess: () => <CopySuccessAction>{ type: ActionKeys.CopyUnitSuccess },
    receivedUnitRoutes: (routes: RouteModel[]) => <ReceivedUnitRoutesAction>{ type: ActionKeys.ReceivedUnitRoutes, routes },
    selectUnit: (unit: UnitModel | null) => <SelectUnitAction>{ type: ActionKeys.UnitSelected, selected: unit },
    sendEmail: (unit: UnitModel) => <SendEmailAction>{ type: ActionKeys.SendEmail, unit },
    sendEmailStarted: (unit: UnitModel) => <SendEmailStartedAction>{ type: ActionKeys.SendEmailStarted, unit },
    sendEmailSubmitted: (data: any) => <SendEmailSubmittedAction>{ type: ActionKeys.SendEmailSubmitted, data },
    sendEmailSuccess: () => <SendEmailSuccessAction>{ type: ActionKeys.SendEmailSuccess },
    sendEmailError: () => <SendEmailErrorAction>{ type: ActionKeys.SendEmailError },
    sendEmailCancelled: () => <SendEmailCancelledAction>{ type: ActionKeys.SendEmailCancelled },
    sendEmailToReporter: (unit: UnitModel) => <SendEmailToReporterAction>{ type: ActionKeys.SendEmailToReporter, unit },
    sendEmailToReporterStarted: (unit: UnitModel) => <SendEmailToReporterStartedAction>{ type: ActionKeys.SendEmailToReporterStarted, unit },
    sendEmailToReporterSubmitted: (data: any) => <SendEmailToReporterSubmittedAction>{ type: ActionKeys.SendEmailToReporterSubmitted, data },
    sendEmailToReporterSuccess: () => <SendEmailToReporterSuccessAction>{ type: ActionKeys.SendEmailToReporterSuccess },
    sendEmailToReporterCancelled: () => <SendEmailToReporterCancelledAction>{ type: ActionKeys.SendEmailToReporterCancelled },
    sendEmailToReporterError: () => <SendEmailToReporterErrorAction>{ type: ActionKeys.SendEmailToReporterError },
    ResetAddNewUnitFormTrailerIdentifier: () => <ResetAddNewUnitFormTrailerIdentifierAction>{ type: ActionKeys.ResetAddNewUnitFormTrailerIdentifier }
}

interface UnitRoutes {
    routes: RouteModel[];
}
// State
export type FreeUnitState = ResourceLoadingState<UnitModel> &
    AddEditResourceState<UnitModel> &
    DeleteResourceState<UnitModel> &
    SelectResourceState<UnitModel> &
    UnitRoutes &
    CachedFiltersState &
    EditMode &
    SendEmailState<UnitModel>;


const initialState: FreeUnitState = {
    isLoading: false,
    data: [],
    isEditing: false,
    isDeleting: false,
    filters: undefined,
    routes: [],
    selected: null,
    emailItem: null,
    isSendingEmail: false,
    isSendingEmailToReporter: false
}

type FreeUnitAction = 
    FreeUnitsRequestAction
    | FreeUnitsReceivedAction
    | AddEditUnitStartedAction
    | AddNewFreeUnitSuccessAction
    | ReceivedUnitRoutesAction
    | CacheFiltersAction
    | DeleteUnitStartedAction
    | DeleteUnitSuccessAction
    | CopyStartedAction
    | CopySuccessAction
    | SelectUnitAction
    | SendEmailStartedAction
    | SendEmailSuccessAction
    | SendEmailCancelledAction
    | SendEmailToReporterStartedAction
    | SendEmailToReporterSuccessAction
    | SendEmailToReporterCancelledAction;

export const reducer: Reducer<FreeUnitState> = (state: FreeUnitState, action: FreeUnitAction) => {
    switch (action.type) {
        case ActionKeys.RequestFreeUnits:
            return {
                ...state,
                isLoading: true,
                data: []
            };
        case ActionKeys.ReceivedFreeUnits:
            return {
                ...state,
                isLoading: false,
                data: action.data
            }
        case ActionKeys.AddNewFreeUnitStarted:
            return {
                ...state,
                isEditing: true,
                editedItem: action.unit,
                editMode: action.unit ? "edit" : "add"
            }
        case ActionKeys.AddNewFreeUnitSuccess:
            return {
                ...state,
                isEditing: false,
                editedItem: undefined,
                editMode: undefined
            }
        case ActionKeys.CacheFilters:
            return {
                ...state,
                filters: action.filters
            }
        case ActionKeys.DeleteStarted:
            return {
                ...state,
                isDeleting: true,
                toDeleteItem: action.unit
            }
        case ActionKeys.DeleteSuccess:
            return {
                ...state,
                isDeleting: false,
                toDeleteItem: undefined
            }
        case ActionKeys.CopyUnitStarted:
            return {
                ...state,
                isEditing: true,
                editedItem: action.unit,
                editMode: "copy"
            };
        case ActionKeys.CopyUnitSuccess:
            return {
                ...state,
                isEditing: false,
                editedItem: undefined,
                editMode: undefined
            }
        case ActionKeys.ReceivedUnitRoutes:
            const newRoutes = [...state.routes, ...action.routes];
            return {
                ...state,
                routes: newRoutes
            }
        case ActionKeys.UnitSelected:
            return {
                ...state,
                selected: action.selected,
            }
        case ActionKeys.SendEmailStarted:
            return {
                ...state,
                isSendingEmail: true,
                emailItem: action.unit
            }
        case ActionKeys.SendEmailSuccess:
            return {
                ...state,
                emailItem: null,
                isSendingEmail: false
            }
        case ActionKeys.SendEmailCancelled:
            return {
                ...state,
                emailItem: null,
                isSendingEmail: false
            }
        case ActionKeys.SendEmailToReporterStarted:
            return {
                ...state,
                isSendingEmailToReporter: true,
                emailItem: action.unit
            }
        case ActionKeys.SendEmailToReporterSuccess:
            return {
                ...state,
                emailItem: null,
                isSendingEmailToReporter: false
            }
        case ActionKeys.SendEmailToReporterCancelled:
            return {
                ...state,
                emailItem: null,
                isSendingEmailToReporter: false
            }
        default:
            const exhaustiveCheck: never = action;
    }

    return state || initialState;
}


// WEBPACK FOOTER //
// ./src/units/store/unitStore.ts